import { VideoLog } from '@features/video/lib/log';
import { CleanAudio, NoiseSuppressor } from '@features/video/lib/rnnoise';
import { MicIssue } from '@features/video/types';

// Звонок при открытой странице виден только по таймеру
const CHECK_MS = 5000;

type Target = {
  rnnoise: NoiseSuppressor | null;
  // сырой трек на входе шумодава
  raw: () => MediaStreamTrack | null;
  // есть что сторожить: трек опубликован (игрок) или цепочка собрана (источник судьи)
  active: () => boolean;
  micOn: () => boolean;
  onIssue: (issue: MicIssue) => void;
  // цепочка собрана заново: владелец подменяет трек в эфире и индикатор
  onRebuilt: (clean: CleanAudio) => Promise<void>;
  // журнал клиента: лениво, клиент создает сторожа до журнала
  log: () => VideoLog;
};

// Сторож микрофона (по демо rnnoiseRebuild). Прерывание аудиосессии — звонок, другое
// приложение — убивает контекст шумодава молча: state running, а в эфир идет тишина.
// Проверяем по таймеру и при возврате на экран (в фоне таймеры заморожены). Захват
// отобрали (трек ended или muted) — чиним не мы, нужен новый трек от хука устройств.
// Контекст замерз — цепочка заново и подмена трека в публикации; без жеста новый контекст
// может не запуститься, тогда нужен клик. Контекст ждет клика (перезагрузка без клика) —
// вне жеста не пересобираем, только плашка; клиент на это время мьютит трек в эфире.
// Клик по плашке или включение микрофона — проверка в жесте: контекст не проснулся и в нем,
// значит беда не в клике, а в самом контексте — пересобираем цепочку на новом (так чинил
// отбой с новым входом в Firefox на Android, решение 31). Выключенный микрофон чиним молча
export class MicWatchdog {
  private timer: number | null = null;
  // проверки по одной: клик не обгоняет идущую проверку, а встает за ней
  private inflight: Promise<void> = Promise.resolve();
  private issue: MicIssue = null;

  constructor(private readonly target: Target) {}

  start(): void {
    this.stop();
    this.timer = window.setInterval(() => this.check(), CHECK_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.check();
  }

  // Остановка — и при подмене трека (новое устройство, восстановление после звонка): показанную
  // беду снимаем с уведомлением, иначе хук держит плашку, пока следующая проверка не найдет
  // новую
  stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.set(null);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // Клик по кнопке плашки или включение микрофона: будим контекст синхронно, пока жив жест,
  // и проверяем как в жесте. Состояние до и после — в журнал, когда есть что чинить
  wake(): void {
    const { rnnoise } = this.target;
    const worth = this.issue !== null || (rnnoise !== null && !rnnoise.isRunning);
    if (worth) this.target.log().event('mic_fix', this.snapshot());
    rnnoise?.wake();
    this.check(true).then(() => {
      if (worth) this.target.log().event('mic_fix_result', this.snapshot());
    });
  }

  // gesture — проверка из жеста пользователя: контексту можно родиться заново
  check(gesture = false): Promise<void> {
    this.inflight = this.inflight.then(() => this.run(gesture));
    return this.inflight;
  }

  private async run(gesture: boolean): Promise<void> {
    const { rnnoise } = this.target;
    const raw = this.target.raw();
    if (!raw || !this.target.active()) return;
    try {
      if (raw.readyState === 'ended' || raw.muted) {
        this.set('lost');
      } else if (!rnnoise?.isAttached) {
        this.set(null);
      } else if (!(await rnnoise.tryResume())) {
        if (gesture) await this.rebuild(rnnoise, raw, 'stuck');
        else this.set('gesture');
      } else if (await rnnoise.isFrozen()) {
        await this.rebuild(rnnoise, raw, 'frozen');
      } else {
        this.set(null);
      }
    } catch (e) {
      this.target.log().error('mic_repair_failed', e);
      this.set('gesture');
    }
  }

  // Цепочка заново на новом контексте и подмена трека у владельца. Не запустился и новый:
  // после замерзания — снова плашка про клик; в жесте — беда в захвате ('stuck'), владелец
  // закрывает контекст и берет захват заново
  private async rebuild(
    rnnoise: NoiseSuppressor,
    raw: MediaStreamTrack,
    reason: 'stuck' | 'frozen',
  ): Promise<void> {
    const clean = await rnnoise.rebuild(raw);
    await this.target.onRebuilt(clean);
    this.target.log().event('mic_rebuilt', { reason, ...this.snapshot() });
    this.set(rnnoise.isRunning ? null : reason === 'stuck' ? 'stuck' : 'gesture');
  }

  private onVisibility = (): void => {
    if (!document.hidden) this.check();
  };

  // Обстановка для журнала: беда, контекст и его последняя ошибка, сырой трек, видимость
  // страницы — по ним разбираются беды на устройствах, которых нет под рукой
  private snapshot(): Record<string, unknown> {
    const { rnnoise } = this.target;
    const raw = this.target.raw();
    return {
      issue: this.issue ?? 'none',
      ctx: rnnoise?.state ?? 'none',
      error: rnnoise?.lastError ?? 'none',
      raw: !raw ? 'none' : raw.readyState === 'ended' ? 'ended' : raw.muted ? 'muted' : 'live',
      hidden: document.hidden,
    };
  }

  private set(issue: MicIssue): void {
    const shown = this.target.micOn() ? issue : null;
    if (this.issue === shown) return;
    this.target.log().event('mic_issue', { ...this.snapshot(), issue: shown ?? 'none' });
    this.issue = shown;
    this.target.onIssue(shown);
  }
}
